import { joinURL, encodeQueryItem } from 'ufo';
import { createOperationsGenerator, defineProvider } from '@nuxt/image/runtime';
import type { AssetDataBynder, AssetDataBynderVideo, AssetDataKontentAi, AssetDataYouTubeVideo, AssetDataIcon, Asset } from '~/@types/cms';
import type { SharedImageProps } from '~/components/atm/Image.vue';

export const ASSET_BASE_PATH = 'https://assets.woom.com';
export const MEDIAHUB_BASE_PATH = 'https://mediahub.woom.com';
export const KONTENTAI_BASE_PATH = 'https://assets-eu-01.kc-usercontent.com';
export const SHOPIFY_ASSET_BASE_PATH = 'https://cdn.shopify.com';

export function getAssetUrl(imageName: string, category = 'products') {
    return `${ASSET_BASE_PATH}/${category}/${imageName}.png`;
}

export const nuxtImageSizes = {
    carousel: '400 md:512 lg:720 2xl:1200',
};

export function isAssetBynder(asset: { id: string }): asset is AssetDataBynder {
    return asset.id.startsWith('b:');
}
export function isAssetDataBynderVideo(asset: { id: string }): asset is AssetDataBynderVideo {
    return asset.id.startsWith('video:');
}
export function isAssetDataYouTubeVideo(asset: { id: string }): asset is AssetDataYouTubeVideo {
    return asset.id.startsWith('yt:');
}
export function isAssetKontentAi(asset: { id: string }): asset is AssetDataKontentAi {
    return asset.id.startsWith('a:');
}
export function isAssetDataIcon(asset: { id: string }): asset is AssetDataIcon {
    return asset.id.startsWith('icon:');
}

export const resolveAssetSrc = (
    asset: Asset,
    bynderType: 'image' | '1to1' | '16to9' | '9to16' | '2to3' | '3to2' | 'png_high_res' = 'image',
): string | undefined => {
    if (typeof asset === 'string' || !asset) return;
    if (isAssetKontentAi(asset)) return asset.data.src;
    else if (isAssetBynder(asset))
        return `${asset.data.baseUrl}${asset.data.png_high_res || asset.data[bynderType] ? asset.data[bynderType] : asset.data.image}`;
};

const getAssetSrc = ({ src, data, preset }: Pick<SharedImageProps, 'src' | 'data' | 'preset'>) => {
    if (!data) return src;
    if (typeof data === 'string') return data;

    if (typeof data !== 'object' || isAssetDataIcon(data)) return;

    if (isAssetDataYouTubeVideo(data)) return;
    if (isAssetKontentAi(data)) {
        return data.data.src;
    }
    const withBaseUrl = (imagePath: string) => `${data.data.baseUrl || ''}${imagePath}`;

    if (isAssetBynder(data)) {
        const imageData = data.data;
        type ImageDataKeys = keyof typeof imageData;

        if (imageData.original?.endsWith('.png')) {
            return withBaseUrl(imageData.original);
        }

        const getBestPreset = (presets: string[]) => (presets.find((preset) => imageData[preset as ImageDataKeys]) || 'image') as ImageDataKeys;
        // prefer png_high_res if available
        const presetMap: Record<string, string[]> = {
            '1to1': ['png_high_res', '1to1', '3to2'],
            '3to2': ['png_high_res', '3to2'],
            '2to3': ['png_high_res', '2to3', '1to1', '3to2'],
            '16to9': ['png_high_res', '16to9', '3to2'],
        };
        const imagePreset = getBestPreset(presetMap[preset || '16to9'] || []);
        return withBaseUrl(imageData[imagePreset] as string);
    }
    if (isAssetDataBynderVideo(data)) {
        return withBaseUrl(data.data.image);
    }
    console.warn('Unknown Asset', data);
};

/**
 * keep in sync with ./image-providers/
 *
 * 📘 docs: https://image.nuxt.com/advanced/custom-provider
 */
type AssetProvider = 'mediahub' | 'assets' | 'kentico' | 'shpfy';
// TODO: switch to type ConfiguredImageProviders, when https://github.com/nuxt/image/issues/2014 is resolved
export const getAssetProvider = (src?: string, noTransform = false): AssetProvider | undefined => {
    if (noTransform || !src) return;
    if (src.startsWith(MEDIAHUB_BASE_PATH)) return 'mediahub';
    if (src.startsWith(ASSET_BASE_PATH)) return 'assets';
    if (src.startsWith(KONTENTAI_BASE_PATH)) return 'kentico';
    if (src.startsWith(SHOPIFY_ASSET_BASE_PATH)) return 'shpfy';
};

export const getAssetInfo = ({ src, data, preset, noTransform }: Pick<SharedImageProps, 'src' | 'data' | 'preset' | 'noTransform'>) => {
    const _src = getAssetSrc({ src, data, preset });
    const provider = getAssetProvider(_src, noTransform);

    return {
        /**
         * will be implemented in https://onewoom.atlassian.net/browse/ECOMM-3030
         */
        base64Image: '',
        provider,
        src: _src,
    };
};
const createAspectModifiers = (height: number) => ({ height: Math.floor(height), fit: 'cover' });
export const getAssetAspectModifiers = ({
    width,
    preset,
}: { width?: number } & Pick<SharedImageProps, 'preset'>): { height?: number; fit?: string } => {
    if (!width || !preset) return {};
    if (preset === '16to9') return createAspectModifiers((width / 16) * 9);
    if (preset === '3to2') return createAspectModifiers((width / 3) * 2);
    if (preset === '2to3') return createAspectModifiers((width / 2) * 3);
    if (preset === '1to1') return createAspectModifiers(width);
    return {};
};

const cloudflareOperationsGenerator = createOperationsGenerator({
    keyMap: {
        width: 'w',
        height: 'h',
        dpr: 'dpr',
        fit: 'fit',
        gravity: 'g',
        quality: 'q',
        format: 'f',
        sharpen: 'sharpen',
    },
    valueMap: {
        fit: {
            cover: 'cover',
            contain: 'contain',
            fill: 'scale-down',
            outside: 'crop',
            inside: 'pad',
        },
        gravity: {
            auto: 'auto',
            side: 'side',
        },
    },
    joinWith: ',',
    formatter: (key: string, val: string) => encodeQueryItem(key, val),
});

const cloudflareDefaultModifiers = {};

/**
 * https://developers.cloudflare.com/images/image-resizing/url-format/
 */
export const genericCloudflareProvider = (baseURL: string) =>
    defineProvider({
        getImage(src, { modifiers }) {
            src = src.startsWith(baseURL) ? src.substring(baseURL.length) : src;
            const aspectModifiers = getAssetAspectModifiers(modifiers);
            const mergeModifiers = { ...cloudflareDefaultModifiers, ...modifiers, ...aspectModifiers };
            const operations = cloudflareOperationsGenerator(mergeModifiers);

            // https://<ZONE>/cdn-cgi/image/<OPTIONS>/<SOURCE-IMAGE>
            const url = operations ? joinURL(baseURL, '/cdn-cgi/image', operations, src) : joinURL(baseURL, src);

            return {
                url,
            };
        },
    });
